import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../api/base';

type LocalizedText = { ru?: string; ky?: string; en?: string } | string | null;

interface GalleryItem {
    id: number;
    eventId: number | null;
    eventName: LocalizedText;
    url: string;
    caption: LocalizedText;
    displayOrder: number;
    type: 'image' | 'video';
    createdAt: string;
}

interface EventOption {
    id: number;
    name: LocalizedText;
    slug: string;
    isActiveDefault?: boolean;
}

const LazyImage: React.FC<{ src: string; alt: string; className?: string; priority?: boolean }> = ({ src, alt, className, priority }) => {
    const [imageSrc, setImageSrc] = useState<string>(priority ? src : '');
    const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (priority) return;
        let observer: IntersectionObserver | undefined;

        if (imageRef) {
            observer = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && !imageSrc) {
                            setImageSrc(src);
                            observer?.unobserve(imageRef);
                        }
                    });
                },
                { threshold: 0.01, rootMargin: '300px' }
            );
            observer.observe(imageRef);
        }

        return () => {
            if (observer && imageRef) {
                observer.unobserve(imageRef);
            }
        };
    }, [imageRef, imageSrc, src, priority]);

    return (
        <div className={`relative w-full h-full ${className || ''}`}>
            {!isLoaded && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[20px]" />
            )}
            <img
                ref={setImageRef}
                src={imageSrc || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3C/svg%3E'}
                alt={alt}
                className={`w-full h-full object-cover rounded-[20px] transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setIsLoaded(true)}
                loading="lazy"
                decoding="async"
            />
        </div>
    );
};

interface LightboxProps {
    items: { id: number; url: string; alt: string }[];
    index: number;
    onClose: () => void;
    onPrev: () => void;
    onNext: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ items, index, onClose, onPrev, onNext }) => {
    useEffect(() => {
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') onPrev();
            else if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, onPrev, onNext]);

    // Swipe для мобильных
    const touchRef = React.useRef<{ x: number; y: number } | null>(null);
    const handleTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchRef.current = { x: t.clientX, y: t.clientY };
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!touchRef.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchRef.current.x;
        const dy = t.clientY - touchRef.current.y;
        touchRef.current = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            if (dx > 0) onPrev(); else onNext();
        }
    };

    const item = items[index];
    if (!item) return null;
    const hasMultiple = items.length > 1;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/[.92]"
            onClick={onClose}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label="Close"
                className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 transition"
            >
                ✕
            </button>

            {hasMultiple && (
                <button
                    onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    aria-label="Previous"
                    className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 text-white text-2xl md:text-3xl hover:bg-white/20 transition"
                >
                    ‹
                </button>
            )}

            {hasMultiple && (
                <button
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                    aria-label="Next"
                    className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 text-white text-2xl md:text-3xl hover:bg-white/20 transition"
                >
                    ›
                </button>
            )}

            <img
                key={item.id}
                src={item.url}
                alt={item.alt}
                onClick={(e) => e.stopPropagation()}
                className="max-w-[92vw] max-h-[88vh] object-contain select-none"
                draggable={false}
            />

            {hasMultiple && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/40 px-3 py-1 rounded-full">
                    {index + 1} / {items.length}
                </div>
            )}
        </div>,
        document.body
    );
};

function getLocalized(text: LocalizedText, lang: string): string {
    if (!text) return '';
    if (typeof text === 'string') return text;
    if (typeof text === 'object') {
        const langKey = lang as keyof typeof text;
        return (text[langKey] || text.ru || text.en || text.ky || '') as string;
    }
    return String(text);
}

const PAGE_SIZE = 8;

function GallerySection() {
    const { t, i18n } = useTranslation();
    const [items, setItems] = useState<GalleryItem[]>([]);
    const [events, setEvents] = useState<EventOption[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string>('all');
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_URL}/api/events`)
            .then(async ({ data }) => {
                if (cancelled) return;
                const list: EventOption[] = (data?.events || []).map((e: any) => ({
                    id: e.id,
                    name: e.name,
                    slug: e.slug,
                    isActiveDefault: e.isActiveDefault,
                }));
                setEvents(list);
                const defaultEvent = list.find(e => e.isActiveDefault);
                if (!defaultEvent) return;
                try {
                    const { data: g } = await axios.get(`${API_URL}/api/gallery`, {
                        params: { limit: '1', offset: '0', eventId: String(defaultEvent.id) }
                    });
                    if (cancelled) return;
                    const hasPhotos = Array.isArray(g?.items) ? g.items.length > 0 : (g?.total || 0) > 0;
                    if (hasPhotos) setSelectedEventId(String(defaultEvent.id));
                } catch (err) {
                    console.error('Error probing gallery for default event:', err);
                }
            })
            .catch(err => {
                console.error('Error fetching events for gallery filter:', err);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        setItems([]);

        const params: Record<string, string> = {
            limit: String(PAGE_SIZE),
            offset: '0',
        };
        if (selectedEventId !== 'all') {
            params.eventId = selectedEventId;
        }

        axios.get(`${API_URL}/api/gallery`, { params })
            .then(({ data }) => {
                if (cancelled) return;
                setItems(data?.items || []);
                setTotal(data?.total || 0);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Error fetching gallery:', err);
                setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [selectedEventId]);

    const loadMore = () => {
        if (loadingMore || items.length >= total) return;
        setLoadingMore(true);
        const params: Record<string, string> = {
            limit: String(PAGE_SIZE),
            offset: String(items.length),
        };
        if (selectedEventId !== 'all') {
            params.eventId = selectedEventId;
        }
        axios.get(`${API_URL}/api/gallery`, { params })
            .then(({ data }) => {
                setItems(prev => [...prev, ...(data?.items || [])]);
                if (typeof data?.total === 'number') setTotal(data.total);
            })
            .catch(err => {
                console.error('Error loading more gallery:', err);
            })
            .finally(() => setLoadingMore(false));
    };

    const hasMore = items.length < total;

    // При открытии lightbox — догружаем все оставшиеся картинки в фоне,
    // чтобы юзер мог листать всю галерею стрелками.
    useEffect(() => {
        if (lightboxIndex === null) return;
        if (items.length >= total) return;
        if (loadingMore) return;

        const fetchAll = async () => {
            setLoadingMore(true);
            let current = items.length;
            const accumulated: GalleryItem[] = [];
            try {
                while (current + accumulated.length < total) {
                    const params: Record<string, string> = {
                        limit: '50',
                        offset: String(current + accumulated.length),
                    };
                    if (selectedEventId !== 'all') params.eventId = selectedEventId;
                    const { data } = await axios.get(`${API_URL}/api/gallery`, { params });
                    const batch: GalleryItem[] = data?.items || [];
                    if (batch.length === 0) break;
                    accumulated.push(...batch);
                }
                if (accumulated.length > 0) {
                    setItems(prev => [...prev, ...accumulated]);
                }
            } catch (err) {
                console.error('Error preloading gallery for lightbox:', err);
            } finally {
                setLoadingMore(false);
            }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxIndex]);

    const lightboxItems = useMemo(
        () => items.map((it, idx) => ({
            id: it.id,
            url: it.url.startsWith('http') ? it.url : `${API_URL}${it.url}`,
            alt: getLocalized(it.caption, i18n.language)
                || getLocalized(it.eventName, i18n.language)
                || `${t('gallery.title')} ${idx + 1}`,
        })),
        [items, i18n.language, t]
    );

    const handlePrev = useCallback(() => {
        setLightboxIndex(i => (i === null ? null : (i - 1 + lightboxItems.length) % lightboxItems.length));
    }, [lightboxItems.length]);

    const handleNext = useCallback(() => {
        setLightboxIndex(i => (i === null ? null : (i + 1) % lightboxItems.length));
    }, [lightboxItems.length]);

    const mediaUrl = (url: string) => (url.startsWith('http') ? url : `${API_URL}${url}`);

    const itemsWithAlt = useMemo(
        () => items.map((it, idx) => ({
            ...it,
            altText: getLocalized(it.caption, i18n.language)
                || getLocalized(it.eventName, i18n.language)
                || `${t('gallery.title')} ${idx + 1}`,
        })),
        [items, i18n.language, t]
    );

    return (
        <section id="gallery" style={{ background: '#DFEFCB', padding: '64px 24px' }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                    <span className="section-tag">{t('gallery.tag', 'Живые моменты')}</span>
                </div>
                <h2 className="h2-center" style={{ marginBottom: 24 }}>
                    {t('gallery.title')}
                </h2>

                {events.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                        <select
                            className="f"
                            style={{ maxWidth: 240 }}
                            value={selectedEventId}
                            onChange={(e) => setSelectedEventId(e.target.value)}
                            aria-label={t('gallery.filter_label') as string}
                        >
                            <option value="all">{t('gallery.all_events')}</option>
                            {events.map(ev => (
                                <option key={ev.id} value={ev.id}>
                                    {getLocalized(ev.name, i18n.language) || ev.slug}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {loading ? (
                    <div className="grid-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} style={{ aspectRatio: '1', background: 'rgba(255,255,255,.5)', borderRadius: 22 }} />
                        ))}
                    </div>
                ) : error ? (
                    <p style={{ textAlign: 'center', color: '#5a6b4d', padding: '48px 0' }}>{t('gallery.error')}</p>
                ) : itemsWithAlt.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#5a6b4d', padding: '48px 0' }}>{t('gallery.empty')}</p>
                ) : (
                    <>
                        <div className="grid-4">
                            {itemsWithAlt.map((item, idx) => (
                                <button
                                    type="button"
                                    key={item.id}
                                    onClick={() => setLightboxIndex(idx)}
                                    aria-label={item.altText}
                                    style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
                                >
                                    <div style={{ aspectRatio: '1', overflow: 'hidden', borderRadius: 22 }} className="gimg">
                                        <LazyImage
                                            src={mediaUrl(item.url)}
                                            alt={item.altText}
                                            priority={idx < 2}
                                        />
                                    </div>
                                </button>
                            ))}
                        </div>

                        {hasMore && (
                            <div style={{ textAlign: 'center', marginTop: 26 }}>
                                <button
                                    className="ghost-btn"
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? t('gallery.loading_more') : t('gallery.load_more')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {lightboxIndex !== null && lightboxItems[lightboxIndex] && (
                <Lightbox
                    items={lightboxItems}
                    index={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onPrev={handlePrev}
                    onNext={handleNext}
                />
            )}
        </section>
    );
}

export default GallerySection;
